import { PopoverOrDialog } from '@/components/ui/popover-or-dialog';
import { CellContext } from '@tanstack/react-table';
import { ArrowRight, Link2 } from 'lucide-react';
import { useState } from 'react';
import { BankSyncData } from './bank-account-table';
import { SimpleFinAccountForm } from './simple-fin-account-form';
import { Account, SimpleFinAccount } from '@liquidbudget/common';
import { getAccountTypeIcon } from '@/components/account-form/account-icon';
import { useIsSmallScreen } from '@/components/ui/breakpoint';

export const renderSimpleFinLinkCell = ({ row }: CellContext<BankSyncData, unknown>) => {
  return <CellSimpleFinLink account={row.original.account} simpleFinAccount={row.original.simpleFinAccount} />;
};

type Props = {
  account: Account;
  simpleFinAccount: SimpleFinAccount | undefined;
};

export function CellSimpleFinLink({ account, simpleFinAccount }: Props) {
  const [open, setOpen] = useState(false);
  const Icon = getAccountTypeIcon(account.type);
  const isSmallScreen = useIsSmallScreen();

  const onClose = () => {
    setOpen(false);
  };

  const renderLinkContent = () => {
    return (
      <div className="flex flex-col gap-2">
        {/* Top row with account name and icon */}
        <div className="flex items-center gap-2 font-semibold dark:text-slate-200">
          {!isSmallScreen && <Icon size={16} className="dark:text-slate-400" />}
          {account.name}

          {!simpleFinAccount && (
            <div className="flex items-center">
              <ArrowRight className="w-3 h-3 text-muted-foreground dark:text-slate-400" />
              <div className="flex flex-col ml-2">
                <span className="text-sm text-muted-foreground dark:text-slate-400">Click to Link</span>
              </div>
            </div>
          )}
        </div>

        {/* Bottom row with SimpleFin info */}
        {simpleFinAccount && (
          <div className="flex items-center">
            <Link2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            <div className="flex items-center ml-2">
              <span className="text-sm text-muted-foreground dark:text-slate-400">{simpleFinAccount.orgName}</span>
              <span className="text-sm text-muted-foreground dark:text-slate-400 mx-1">/</span>
              <span className="text-muted-foreground dark:text-slate-400">{simpleFinAccount.name}</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <PopoverOrDialog
      trigger={
        <div className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 rounded p-1">
          {renderLinkContent()}
        </div>
      }
      title="Link Bank Account"
      open={open}
      setOpen={setOpen}
      popoverClassName="w-80 p-5 w-[var(--radix-popover-trigger-width)]"
    >
      <SimpleFinAccountForm account={account} onClose={onClose} onSuccess={onClose} />
    </PopoverOrDialog>
  );
}
